/**
 * LocationCard - Handles location search, selection mode, and area selection
 */

import type { MapView } from '../map/MapView';
import { RadialSelection } from '../tools/RadialSelection';
import { PostalSearch } from '../tools/PostalSearch';
import { appState } from '../state/AppState';

export class LocationCard {
    private mapView: MapView;
    private radialSelection: RadialSelection;
    private isSelectionModeActive = false;

    constructor(
        mapView: MapView,
        radialSelection: RadialSelection
    ) {
        this.mapView = mapView;
        this.radialSelection = radialSelection;
    }

    render(): string {
        return `
        <div class="card">
            <h3 class="card-header"><i data-lucide="map-pin"></i> Location & Selection</h3>
            <div class="card-body">
                <!-- Input Group: Search + Radius -->
                <div class="input-row">
                    <div class="input-wrapper">
                        <button id="search-btn" class="input-icon-btn" title="Search">
                            <i data-lucide="search"></i>
                        </button>
                        <input type="text" id="postal-input" placeholder="Postal Code or Address" />
                    </div>
                    <div class="input-wrapper suffix" data-suffix="m" style="flex: 0 0 100px;">
                        <input type="number" id="radius-input" value="500" min="100" step="100" />
                    </div>
                </div>

                <!-- Selection Mode -->
                <div style="margin-top: 12px;">
                    <div class="segmented-control">
                        <button class="mode-btn active" data-mode="radial" style="flex: 1">Circle Selection</button>
                        <button class="mode-btn" data-mode="rect" style="flex: 1">Box Selection</button>
                    </div>
                </div>

                <!-- Actions -->
                <div class="btn-row">
                    <button id="select-area-btn" class="btn-primary">
                        <i data-lucide="mouse-pointer-2"></i> Select Area
                    </button>
                    <button id="clear-selection-btn" class="btn-ghost" style="flex: 0 0 auto;">
                        Clear
                    </button>
                </div>
            </div>
        </div>
        `;
    }

    bindEvents(): void {
        this.bindSearchEvents();
        this.bindSelectionControls();
        this.bindClearButton();
        this.bindSelectAreaButton();
    }

    private bindSearchEvents(): void {
        const input = document.getElementById('postal-input') as HTMLInputElement;
        const btn = document.getElementById('search-btn');

        const performSearch = async () => {
            const query = input.value.trim();
            if (query.length < 3) return;

            // Show loading
            btn!.innerHTML = '<i data-lucide="loader" class="animate-spin"></i>';
            // @ts-ignore
            if (window.lucide) window.lucide.createIcons();

            const result = await PostalSearch.search(query);

            // Restore search icon
            btn!.innerHTML = '<i data-lucide="search"></i>';
            // @ts-ignore
            if (window.lucide) window.lucide.createIcons();

            if (result) {
                const lat = parseFloat(result.LATITUDE);
                const lng = parseFloat(result.LONGITUDE);

                // Fly to location
                this.mapView.flyTo(lat, lng);

                // If in radial mode, update selection circle
                if (appState.get('selectionMode') === 'radial') {
                    const radiusInput = document.getElementById('radius-input') as HTMLInputElement;
                    const radius = parseInt(radiusInput.value) || 500;

                    this.radialSelection.setSelection(lat, lng, radius);
                }

                // Persist last search to localStorage
                const radiusInput = document.getElementById('radius-input') as HTMLInputElement;
                try {
                    localStorage.setItem('hdb_lastSearch', JSON.stringify({
                        query: input.value.trim(),
                        radius: radiusInput?.value || '500'
                    }));
                } catch (_) { /* localStorage unavailable */ }
            } else {
                alert('Location not found');
            }
        };

        btn?.addEventListener('click', performSearch);
        input?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') performSearch();
        });

        // Radius Input Visibility
        this.updateRadiusInputVisibility();

        // Adding ENTER support for radius input
        const radiusInput = document.getElementById('radius-input') as HTMLInputElement;
        radiusInput?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') performSearch();
        });

        // Trigger default initial search ONLY on DESKTOP
        const isMobile = window.innerWidth < 768;
        if (!isMobile) {
            // Restore last search from localStorage, or use defaults
            let defaultQuery = "085101";
            let defaultRadius = "888";
            try {
                const saved = localStorage.getItem('hdb_lastSearch');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    defaultQuery = parsed.query || defaultQuery;
                    defaultRadius = parsed.radius || defaultRadius;
                }
            } catch (_) { /* localStorage unavailable */ }

            input.value = defaultQuery;
            if (radiusInput) radiusInput.value = defaultRadius;
            performSearch();
        }
    }

    private bindSelectionControls(): void {
        // Selection Mode Toggle
        const modeBtns = document.querySelectorAll('.mode-btn');
        modeBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = (btn as HTMLElement).dataset.mode as 'radial' | 'rect';
                appState.set('selectionMode', mode);

                // Update UI
                modeBtns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');

                // Update visibility of controls
                this.updateRadiusInputVisibility();

                // Update Map Behavior
                this.mapView.setSelectionType(mode);

                // Reset selection mode if active
                if (this.isSelectionModeActive) {
                    this.setSelectionMode(false);
                }
            });
        });

        // Radius Input Update
        const radiusInput = document.getElementById('radius-input') as HTMLInputElement;
        radiusInput?.addEventListener('change', () => {
            const val = parseInt(radiusInput.value);
            if (val > 0 && this.radialSelection.hasSelection()) {
                const current = this.radialSelection.getCurrentCenter();
                if (current) {
                    this.radialSelection.setSelection(current.lat, current.lng, val);
                }
            }
        });
    }

    private bindClearButton(): void {
        const clearBtn = document.getElementById('clear-selection-btn');
        clearBtn?.addEventListener('click', () => {
            this.radialSelection.clearSelection();

            // Clear Postal Input BUT KEEP Radius
            const postalInput = document.getElementById('postal-input') as HTMLInputElement;
            if (postalInput) postalInput.value = '';
        });
    }

    private bindSelectAreaButton(): void {
        const selectAreaBtn = document.getElementById('select-area-btn');
        selectAreaBtn?.addEventListener('click', () => {
            this.setSelectionMode(!this.isSelectionModeActive);
        });
    }

    setSelectionMode(active: boolean): void {
        this.isSelectionModeActive = active;
        appState.set('isSelectionModeActive', active);
        this.mapView.setSelectionMode(active);

        const btn = document.getElementById('select-area-btn');
        if (btn) {
            btn.classList.toggle('active', active);
            btn.innerHTML = active
                ? '<i data-lucide="x"></i> Cancel Selection'
                : '<i data-lucide="mouse-pointer-2"></i> Select Area';
            // @ts-ignore
            if (window.lucide) window.lucide.createIcons();
        }
    }

    private updateRadiusInputVisibility(): void {
        const radiusInputWrapper = document.getElementById('radius-input')?.closest('.input-wrapper');
        if (radiusInputWrapper) {
            (radiusInputWrapper as HTMLElement).style.display = appState.get('selectionMode') === 'radial' ? 'block' : 'none';
        }
    }

    getIsSelectionModeActive(): boolean {
        return this.isSelectionModeActive;
    }
}
